import { forwardRef, useMemo, type SelectHTMLAttributes } from 'react';
import { SelectField } from '@/components/forms/SelectField';
import { useSectors } from '@/features/startups/hooks/useSectors';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';

type InviteSectorFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  error?: string;
};

/**
 * Optional "Sector / area of work" pick on an Investor member invite
 * (MYN-202, FR-174). Options are the platform `Sector` taxonomy (the one
 * startups choose from) - not the investor industry-preference list. Spread
 * `register('sectorId')` onto it; it is mounted only when
 * `TEAM_PAGE_FEATURES[entityType].inviteSector` is on, so other entity types
 * never fetch the taxonomy from this dialog.
 */
export const InviteSectorField = forwardRef<HTMLSelectElement, InviteSectorFieldProps>(
  function InviteSectorField({ error, ...selectProps }, ref) {
    const { data: sectors = [] } = useSectors();
    const options = useMemo(
      () => sectors.map((sector) => ({ value: String(sector.id), label: sector.name })),
      [sectors],
    );
    return (
      <SelectField
        ref={ref}
        label={TEAM_STRINGS.INVITE_SECTOR.LABEL}
        placeholder={TEAM_STRINGS.INVITE_SECTOR.PLACEHOLDER}
        optional
        options={options}
        error={error}
        {...selectProps}
      />
    );
  },
);
